import React from 'react';
import { ViewFilterOptions } from './CreateCustomView';
import { Box, IconButton, OutlinedInput } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { ViewCondition } from 'types/userConfig/userConfig';
import useLocalization from 'hooks/useLocalization';
import { DateTimePicker } from '@mui/x-date-pickers';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import dayjs from 'dayjs';

interface CreateCustomViewConditionsProps {
  options: Array<ViewFilterOptions>;
  conditions: ViewCondition[];
  addNewCondition: () => void;
  deleteCondition: (id: number | string) => void;
  onChange: any;
}

const CreateCustomViewConditions: React.FC<CreateCustomViewConditionsProps> = ({
  options,
  conditions,
  addNewCondition,
  deleteCondition,
  onChange,
}) => {
  //* localization
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(
    () => currentLocale.settingsPopup.navigation.filters.create,
    [currentLocale],
  );

  return (
    <Box>
      <Grid container spacing={1.5} alignItems={'center'}>
        {conditions.map((condition) => {
          const currentConditionOption = options.find((option) => option.key === condition.key);

          return (
            <Grid size={12} key={condition.id}>
              <Grid
                container
                spacing={1}
                alignItems={'center'}
                // flexWrap={"nowrap"}
              >
                <Grid size="grow">
                  <FormControl fullWidth>
                    <InputLabel id={`condition-${condition.id}-key-label`}>
                      {localization.keyLabel}
                    </InputLabel>
                    <Select
                      labelId={`condition-${condition.id}-key-label`}
                      id={`condition-${condition.id}-key`}
                      value={condition.key}
                      label={localization.keyLabel}
                      onChange={(e) =>
                        onChange(condition.id, {
                          key: e.target.value,
                          value: null,
                        })
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // גובה של 5 שורות בערך
                          },
                        },
                      }}
                    >
                      {options.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size="grow">
                  {currentConditionOption && (
                    <React.Fragment>
                      {currentConditionOption.type === 'select' && (
                        <FormControl fullWidth>
                          <InputLabel id={`condition-${condition.id}-value-label`}>
                            {localization.valueLabel}
                          </InputLabel>
                          <Select
                            labelId={`condition-${condition.id}-value-label`}
                            id={`condition-${condition.id}-value`}
                            value={condition.value || (currentConditionOption.multiple ? [] : '')}
                            label={localization.valueLabel}
                            onChange={(e) => {
                              const value = e.target.value;

                              onChange(condition.id, {
                                value:
                                  currentConditionOption.multiple && typeof value === 'string'
                                    ? value.split(',')
                                    : value,
                              });
                            }}
                            multiple={currentConditionOption.multiple ?? false}
                            input={
                              currentConditionOption.multiple ? <OutlinedInput label="Value" /> : undefined
                            }
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200, // גובה של 5 שורות בערך
                                },
                              },
                            }}
                          >
                            {currentConditionOption.options &&
                              currentConditionOption.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                      {currentConditionOption.type === 'input' && (
                        <TextField
                          fullWidth
                          label={localization.valueLabel}
                          value={condition.value || ''}
                          onChange={(e) =>
                            onChange(condition.id, {
                              value: e.target.value,
                            })
                          }
                        />
                      )}
                      {currentConditionOption.type === 'dateTime' && (
                        <DateTimePicker
                          label={localization.valueLabel}
                          value={dayjs(condition.value)}
                          onChange={(newValue) =>
                            onChange(condition.id, {
                              value: newValue?.toISOString() || null,
                            })
                          }
                          ampm={false}
                          sx={{
                            width: '100%',
                          }}
                        />
                      )}
                    </React.Fragment>
                  )}
                </Grid>
                <Grid size={'auto'} textAlign={'right'}>
                  <IconButton color="error" onClick={() => deleteCondition(condition.id)}>
                    <DeleteOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid size={12}>
          <Button variant="contained" onClick={addNewCondition}>
            {localization.add}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateCustomViewConditions;
